import React, {useEffect, useState} from 'react';
import './ShareableLinksDownloadFlow.scss';
import {Box, Typography} from '@mui/material';
import {ReactComponent as DownloadGradient} from '../../../../assets/images/download_bg.svg';
import {ReactComponent as AuroraLogo} from '../../../../../public/aurora-review-logo.svg';
import {t} from 'i18next';
import {useNavigate, useParams} from 'react-router-dom';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrButton from '../../../common/arr-button/ArrButton';
import {
  useValidateDownload,
  useValidateDownloadURI,
} from '../../../../hooks/productions/useProductions';
import {ValidateDownloadRequest} from '../../../../api/types/production.type';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';

const DOWNLOAD_ERRORS = {
  INVALID_PASSWORD: 'Invalid Password',
  LINK_IS_EXPIRED: 'Link is Expired',
  LINK_EXPIRED: 'LinkExpired',
};

const DOWNLOAD_ROUTES = {
  LINK_EXPIRED: '/Production/LinkExpired',
  DOWNLOAD_ERROR: '/Production/DownloadError',
};

const DOWNLOAD_TYPE = {
  PROD: 'prod',
  ARCHIVE: 'A',
  DAT: 'D',
} as const;

const DEFAULT_IP = '';

const ShareableLinksDownloadFlow: React.FC = () => {
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(true);
  const [passwordValue, setPasswordValue] = useState('');
  const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(true);

  const {mutate: downloadProduction} = useValidateDownload();

  const {key} = useParams<{key: string}>();
  const navigate = useNavigate();

  const {
    data: _validateDownloadURIData,
    error: validateDownloadURIError,
    isLoading: isValidateDownloadURILoading,
  } = useValidateDownloadURI(
    {
      key: key ?? '',
      urlHost: window.location.origin,
      absoluteUri: window.location.href,
      ipAddress: DEFAULT_IP,
    },
    !!key && !!window.location
  );

  useEffect(() => {
    if (validateDownloadURIError) {
      const message = validateDownloadURIError.message;
      if (
        message === DOWNLOAD_ERRORS.LINK_EXPIRED ||
        message === DOWNLOAD_ERRORS.LINK_IS_EXPIRED
      ) {
        navigate(DOWNLOAD_ROUTES.LINK_EXPIRED, {replace: true});
      } else {
        navigate(DOWNLOAD_ROUTES.DOWNLOAD_ERROR, {replace: true});
      }
    }
  }, [validateDownloadURIError]);

  const handleDownload = () => {
    const type = window.location.pathname?.split('/')[1];
    const payload: ValidateDownloadRequest = {
      key: key ?? '',
      passcode: passwordValue,
      type:
        type === DOWNLOAD_TYPE.PROD ? DOWNLOAD_TYPE.ARCHIVE : DOWNLOAD_TYPE.DAT,
      validateLinkIpAdd: DEFAULT_IP,
      validateLinkAbsoluteUri: window.location.href,
    };
    downloadProduction(
      {payload},
      {
        onSuccess: () => {},
        onError: (error: any) => {
          const message = error.message;
          if (message === DOWNLOAD_ERRORS.INVALID_PASSWORD) {
            setIsPasswordValid(false);
          } else if (message === DOWNLOAD_ERRORS.LINK_EXPIRED) {
            navigate(DOWNLOAD_ROUTES.LINK_EXPIRED, {replace: true});
          } else {
            navigate(DOWNLOAD_ROUTES.DOWNLOAD_ERROR, {replace: true});
          }
        },
      }
    );
  };

  if (isValidateDownloadURILoading || validateDownloadURIError)
    return <ArrLoadingSpinner isOpen overlay />;
  return (
    <Box className="shareable-links-container">
      <Box className="download-container">
        <DownloadGradient
          className="download-container-gradient"
          aria-hidden="true"
          focusable="false"
        />
        <Box className="download-left">
          <Box className="branding">
            <AuroraLogo />
          </Box>
        </Box>
        <Box className="download-right">
          <Box className="download-right-box">
            <Typography
              id="download-title"
              data-testid="download-title"
              className="typo-text headline2_size download-title"
            >
              {t(
                'productionsAndExports.createNewProduction.downloadLinks.downloadTitle'
              )}
            </Typography>
            <ArrTextField
              id="password"
              type="password"
              dataTestId="sharable-link-password-input"
              state={isPasswordValid ? 'enabled' : 'error'}
              label={t(
                'productionsAndExports.createNewProduction.downloadLinks.enterPassword'
              )}
              value={passwordValue}
              onChange={(e) => {
                const val = e.target.value;
                setPasswordValue(val);
                if (val === '') {
                  setIsDownloadDisabled(true);
                  setIsPasswordValid(true);
                } else {
                  setIsPasswordValid(true);
                  setIsDownloadDisabled(false);
                }
              }}
              supportText={
                !isPasswordValid
                  ? t(
                      'productionsAndExports.createNewProduction.downloadLinks.invalidError'
                    )
                  : ''
              }
            />
            <ArrButton
              variant="primary"
              aria-label={t(
                'productionsAndExports.createNewProduction.downloadLinks.downloadBtn'
              )}
              onClick={handleDownload}
              dataTestId="btn-download"
              disabled={isDownloadDisabled}
            >
              {t(
                'productionsAndExports.createNewProduction.downloadLinks.downloadBtn'
              )}
            </ArrButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ShareableLinksDownloadFlow;
